import api from './client';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf' | 'geojson' | 'shapefile';

const CONTENT_TYPES: Record<ExportFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
  geojson: 'application/geo+json',
  shapefile: 'application/zip',
};

const EXTENSIONS: Record<ExportFormat, string> = {
  csv: '.csv',
  xlsx: '.xlsx',
  pdf: '.pdf',
  geojson: '.geojson',
  shapefile: '.zip',
};

function timestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export async function downloadExport(
  format: ExportFormat,
  sessionData: Record<string, unknown>,
  sessionName: string = 'qenq_export',
) {
  const res = await api.post(`/export/${format}`, sessionData, {
    responseType: 'blob',
  });

  // Try to extract filename from Content-Disposition header
  const disposition = res.headers?.['content-disposition'] as string | undefined;
  let filename: string | undefined;
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }
  // Fallback: build from session name + timestamp
  if (!filename) {
    const safe = sessionName.replace(/[^a-zA-Z0-9_-]/g, '_') || 'qenq_export';
    filename = `${safe}_${timestamp()}${EXTENSIONS[format]}`;
  }

  const blob = new Blob([res.data], { type: CONTENT_TYPES[format] });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function uploadImport(
  format: 'csv' | 'xlsx' | 'geojson' | 'shapefile',
  file: File,
  targetTable?: string,
) {
  const formData = new FormData();
  formData.append('file', file);
  if (targetTable) {
    formData.append('target_table', targetTable);
  }

  const res = await api.post(`/import/${format}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
